import React, { useState, useEffect, useCallback } from 'react';
import { Download, Filter, Search, X } from 'lucide-react';
import * as XLSX from 'xlsx';
import Sidebar from '../components/ObservateurSidebar';

const handleLogout = () => {
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.removeItem('token');
  }
  window.location.href = '/';
};

const API_BASE_URL = '    https://vmi1977988.contaboserver.net/api2';

const ANNEES = ['2023/2024', '2024/2025', '2025/2026', '2026/2027', '2027/2028'];

const StudentPaymentsTracker = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);

  const [anneeScolaire, setAnneeScolaire] = useState('2026/2027');
  const [filiereFilter, setFiliereFilter] = useState('toutes');
  const [classeFilter, setClasseFilter] = useState('toutes');
  const [searchTerm, setSearchTerm] = useState('');

  const filieresSpecifiques = ['IRM', 'MASI', 'CYCLE_INGENIEUR', 'LICENCE_PRO', 'MASTER_PRO'];
  const [filieresDisponibles, setFilieresDisponibles] = useState([]);
  const [classesDisponibles, setClassesDisponibles] = useState([]);

  const getToken = () => localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const token = getToken();
      const headers = { Authorization: `Bearer ${token}` };

      let data;
      try {
        const res = await fetch(
          `${API_BASE_URL}/paiements/suivi-complet?anneeScolaire=${encodeURIComponent(anneeScolaire)}`,
          { headers }
        );
        data = await res.json();
      } catch {
        const res = await fetch(
          `${API_BASE_URL}/paiements/suivi?anneeScolaire=${encodeURIComponent(anneeScolaire)}`,
          { headers }
        );
        data = await res.json();
      }

      const liste = Array.isArray(data) ? data : [];
      setStudents(liste);

      const filieres = [...new Set(
        liste.map(s => s.filiere).filter(f => f && filieresSpecifiques.includes(f))
      )].sort();
      setFilieresDisponibles(filieres);

      const classes = [...new Set(
        liste.flatMap(s => s.cours || [])
      )].filter(Boolean).sort();
      setClassesDisponibles(classes);

    } catch (err) {
      console.error('Erreur fetchData:', err);
    } finally {
      setLoading(false);
    }
  }, [anneeScolaire]);

  useEffect(() => {
    fetchData();
    setFiliereFilter('toutes');
    setClasseFilter('toutes');
  }, [fetchData]);

  const getEtudiantsFiltres = () => {
    return students.filter(s => {
      if (s.desistement?.statut === true || s.desistement?.statut === 'true') return false;
      if (s.aTransfere === false && s.actif === false) return false;

      const term = (searchTerm || '').trim().toLowerCase();
      const searchMatch = !term ||
        s.nomComplet?.toLowerCase().includes(term) ||
        s.codeEtudiant?.toLowerCase().includes(term) ||
        s.prenom?.toLowerCase().includes(term) ||
        s.nomDeFamille?.toLowerCase().includes(term) ||
        s.email?.toLowerCase().includes(term) ||
        s.telephone?.includes(term);

      const matchFiliere = filiereFilter === 'toutes' || s.filiere === filiereFilter;
      const matchClasse = classeFilter === 'toutes' ||
        (s.cours && s.cours.includes(classeFilter));
      return searchMatch && matchFiliere && matchClasse;
    });
  };

  const handleAnneeChange = (val) => {
    setAnneeScolaire(val);
    setFiliereFilter('toutes');
    setClasseFilter('toutes');
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const exportToExcel = () => {
    const liste = getEtudiantsFiltres();

    const excelData = liste.map(s => {
      const prixTotal = parseFloat(s.prixTotal) || 0;
      const totalPaye = parseFloat(s.totalPaye) || 0;
      const reste = parseFloat(s.reste) || Math.max(0, prixTotal - totalPaye);
      const classe = s.cours?.length > 0 ? s.cours.join('; ') : '-';
      return {
        'Code Étudiant': s.codeEtudiant || '-',
        'Nom Complet': s.nomComplet || `${s.prenom || ''} ${s.nomDeFamille || ''}`.trim() || '-',
        'Filière': s.filiere || '-',
        'Classe': classe,
        'Année Scolaire': anneeScolaire,
        'Prix Total (MAD)': prixTotal,
        'Montant Payé (MAD)': totalPaye,
        'Reste à Payer (MAD)': reste,
        'Statut': prixTotal === 0
          ? 'Prix non défini'
          : totalPaye >= prixTotal
            ? 'Complet'
            : totalPaye > 0
              ? 'En cours'
              : 'Non débuté',
      };
    });

    const grandTotalPrix = liste.reduce((s, e) => s + (parseFloat(e.prixTotal) || 0), 0);
    const grandTotalPaye = liste.reduce((s, e) => s + (parseFloat(e.totalPaye) || 0), 0);
    const grandTotalReste = liste.reduce((s, e) => s + (parseFloat(e.reste) || 0), 0);

    excelData.push({
      'Code Étudiant': 'TOTAL',
      'Nom Complet': `${liste.length} étudiants`,
      'Filière': '', 'Classe': '', 'Année Scolaire': anneeScolaire, 'Statut': '',
      'Prix Total (MAD)': grandTotalPrix,
      'Montant Payé (MAD)': grandTotalPaye,
      'Reste à Payer (MAD)': grandTotalReste
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);
    ws['!cols'] = [
      { wch: 18 }, { wch: 30 }, { wch: 15 }, { wch: 35 },
      { wch: 15 }, { wch: 18 }, { wch: 18 }, { wch: 18 }, { wch: 15 }
    ];
    XLSX.utils.book_append_sheet(wb, ws, 'Paiements Étudiants');
    const cleanAnnee = anneeScolaire.replace('/', '-');
    XLSX.writeFile(wb, `etudiants_${cleanAnnee}_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const resetFilters = () => {
    setAnneeScolaire('2026/2027');
    setFiliereFilter('toutes');
    setClasseFilter('toutes');
    setSearchTerm('');
  };

  const etudiantsFiltres = getEtudiantsFiltres();

  // ─── Totaux ───────────────────────────────────────────────────────────────
  const grandTotalPrix = etudiantsFiltres.reduce((s, e) => s + (parseFloat(e.prixTotal) || 0), 0);
  const grandTotalPaye = etudiantsFiltres.reduce((s, e) => s + (parseFloat(e.totalPaye) || 0), 0);
  const grandTotalReste = etudiantsFiltres.reduce((s, e) => s + (parseFloat(e.reste) || 0), 0);

  // ✅ Compteur des étudiants avec prixTotal = 0
  const nbPrixZero = etudiantsFiltres.filter(e => (parseFloat(e.prixTotal) || 0) === 0).length;

  return (
    <div style={{
      background: '#fff', borderRadius: '8px', padding: '2rem',
      marginBottom: '2rem', border: '1px solid #e5e7eb'
    }}>
      <Sidebar onLogout={handleLogout} />

      <h2 style={{
        fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937',
        marginBottom: '1.5rem', textAlign: 'center'
      }}>
        Suivi des Paiements Étudiants
      </h2>

      {/* Filtres */}
      <div style={{
        background: '#f9fafb', borderRadius: '8px', padding: '1.5rem',
        marginBottom: '1.5rem', border: '1px solid #e5e7eb'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
          <Filter size={20} color="#3b82f6" />
          <h3 style={{ fontSize: '1rem', fontWeight: '600', color: '#374151' }}>Filtres</h3>
        </div>

        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem', marginBottom: '1rem'
        }}>

          {/* Recherche */}
          <div>
            <label style={{
              display: 'block', fontSize: '0.875rem', fontWeight: '500',
              color: '#374151', marginBottom: '0.5rem'
            }}>
              Rechercher étudiant
            </label>
            <div style={{ position: 'relative' }}>
              <div style={{
                position: 'absolute', left: '10px', top: '50%',
                transform: 'translateY(-50%)', color: '#6b7280'
              }}>
                <Search size={16} />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Rechercher par code étudiant, nom, prénom..."
                style={{
                  width: '100%', padding: '0.5rem 2.5rem 0.5rem 2.5rem',
                  border: '1px solid #d1d5db', borderRadius: '6px',
                  fontSize: '0.875rem', background: '#fff'
                }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    position: 'absolute', right: '8px', top: '50%',
                    transform: 'translateY(-50%)', background: 'none',
                    border: 'none', color: '#6b7280', cursor: 'pointer', padding: '4px'
                  }}
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Année */}
          <div>
            <label style={{
              display: 'block', fontSize: '0.875rem', fontWeight: '500',
              color: '#374151', marginBottom: '0.5rem'
            }}>Année Scolaire</label>
            <select
              value={anneeScolaire}
              onChange={e => handleAnneeChange(e.target.value)}
              style={{
                width: '100%', padding: '0.5rem', border: '1px solid #d1d5db',
                borderRadius: '6px', fontSize: '0.875rem', background: '#fff'
              }}
            >
              {ANNEES.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          {/* Filière */}
          <div>
            <label style={{
              display: 'block', fontSize: '0.875rem', fontWeight: '500',
              color: '#374151', marginBottom: '0.5rem'
            }}>Filière</label>
            <select
              value={filiereFilter}
              onChange={e => { setFiliereFilter(e.target.value); setClasseFilter('toutes'); }}
              style={{
                width: '100%', padding: '0.5rem', border: '1px solid #d1d5db',
                borderRadius: '6px', fontSize: '0.875rem', background: '#fff'
              }}
            >
              <option value="toutes">Toutes les filières</option>
              {filieresDisponibles.map(f => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>

          {/* Classe */}
          <div>
            <label style={{
              display: 'block', fontSize: '0.875rem', fontWeight: '500',
              color: '#374151', marginBottom: '0.5rem'
            }}>Classe</label>
            <select
              value={classeFilter}
              onChange={e => setClasseFilter(e.target.value)}
              style={{
                width: '100%', padding: '0.5rem', border: '1px solid #d1d5db',
                borderRadius: '6px', fontSize: '0.875rem', background: '#fff'
              }}
            >
              <option value="toutes">Toutes les classes</option>
              {classesDisponibles
                .filter(c => filiereFilter === 'toutes' ||
                  students.some(s => s.filiere === filiereFilter && s.cours?.includes(c)))
                .map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {/* Boutons */}
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button
            onClick={resetFilters}
            style={{
              padding: '0.5rem 1rem', background: '#6b7280', color: '#fff',
              border: 'none', borderRadius: '6px', fontSize: '0.875rem',
              fontWeight: '500', cursor: 'pointer'
            }}
          >
            Réinitialiser
          </button>
          <button
            onClick={fetchData}
            style={{
              padding: '0.5rem 1rem', background: '#3b82f6', color: '#fff',
              border: 'none', borderRadius: '6px', fontSize: '0.875rem',
              fontWeight: '500', cursor: 'pointer'
            }}
          >
            🔄 Actualiser
          </button>
          <button
            onClick={exportToExcel}
            style={{
              padding: '0.5rem 1rem', background: '#10b981', color: '#fff',
              border: 'none', borderRadius: '6px', fontSize: '0.875rem',
              fontWeight: '500', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: '0.5rem'
            }}
          >
            <Download size={16} /> Exporter Excel
          </button>
        </div>

        {/* Résumé */}
        <div style={{
          marginTop: '1rem', padding: '0.75rem', background: '#eff6ff',
          borderRadius: '6px', fontSize: '0.875rem', color: '#1e40af'
        }}>
          <strong>{etudiantsFiltres.length}</strong> étudiant(s) trouvé(s)
          {searchTerm && ` pour "${searchTerm}"`}
          {` • Année: ${anneeScolaire}`}
          {filiereFilter !== 'toutes' && ` • Filière: ${filiereFilter}`}
          {classeFilter !== 'toutes' && ` • Classe: ${classeFilter}`}
        </div>

        {/* ✅ Alerte Prix = 0 */}
        {nbPrixZero > 0 && (
          <div style={{
            marginTop: '0.75rem', padding: '0.75rem', background: '#fffbeb',
            borderRadius: '6px', fontSize: '0.875rem', color: '#92400e',
            border: '1px solid #fcd34d',
            display: 'flex', alignItems: 'center', gap: '0.5rem'
          }}>
            ⚠️ <strong>{nbPrixZero} étudiant(s)</strong> avec Prix Total = 0 MAD
            (non comptés dans le chiffre d'affaire du Dashboard)
          </div>
        )}
      </div>

      {/* Tableau */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#9ca3af' }}>
          Chargement des données pour {anneeScolaire}...
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%', borderCollapse: 'collapse',
            border: '1px solid #d1d5db', fontSize: '0.875rem'
          }}>
            <thead>
              <tr style={{ background: '#f3f4f6' }}>
                <th style={excelHeaderStyle}>Nom Complet</th>
                <th style={excelHeaderStyle}>Code</th>
                <th style={excelHeaderStyle}>Filière</th>
                <th style={excelHeaderStyle}>Classe</th>
                <th style={excelHeaderStyle}>Prix Total (MAD)</th>
                <th style={excelHeaderStyle}>Montant Payé (MAD)</th>
                <th style={excelHeaderStyle}>Reste à Payer (MAD)</th>
              </tr>
            </thead>
            <tbody>
              {etudiantsFiltres.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                    Aucun étudiant trouvé pour {anneeScolaire}
                  </td>
                </tr>
              ) : etudiantsFiltres.map((student, index) => {
                const prixTotal = parseFloat(student.prixTotal) || 0;
                const totalPaye = parseFloat(student.totalPaye) || 0;
                const reste = parseFloat(student.reste) || Math.max(0, prixTotal - totalPaye);
                const classe = student.cours?.length > 0 ? student.cours.join(', ') : '-';
                const estDesiste = student.desistement?.statut === true
                  || student.desistement?.statut === 'true'
                  || student.desistement?.dateDesistement != null;
                const estTransfere = student.aTransfere === true;
                // ✅ Détection prixTotal = 0
                const estPrixZero = prixTotal === 0;

                return (
                  <tr key={student._id} style={{
                    background: estDesiste
                      ? '#fef2f2'
                      : estPrixZero
                        ? '#fffbeb'   // ✅ fond jaune clair pour prix = 0
                        : index % 2 === 0 ? '#ffffff' : '#f9fafb'
                  }}>
                    <td style={excelCellStyle}>
                      <div style={{ fontWeight: 600 }}>{student.nomComplet}</div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{student.codeEtudiant}</div>
                      {estDesiste && (
                        <span style={{
                          fontSize: '0.7rem', color: '#dc2626',
                          background: '#fef2f2', padding: '1px 6px',
                          borderRadius: '4px', display: 'inline-block', marginTop: '2px'
                        }}>
                          🚫 Désisté
                        </span>
                      )}
                      {estTransfere && !estDesiste && (
                        <span style={{
                          fontSize: '0.7rem', color: '#7c3aed',
                          background: '#f5f3ff', padding: '1px 6px',
                          borderRadius: '4px', display: 'inline-block', marginTop: '2px'
                        }}>
                          → {student.anneeTransfert}
                        </span>
                      )}
                      {/* ✅ Badge Prix = 0 */}
                      {estPrixZero && !estDesiste && (
                        <span style={{
                          fontSize: '0.7rem', color: '#92400e',
                          background: '#fef3c7', padding: '1px 6px',
                          borderRadius: '4px', display: 'inline-block', marginTop: '2px',
                          border: '1px solid #fcd34d'
                        }}>
                          ⚠️ Prix non défini
                        </span>
                      )}
                    </td>
                    <td style={{ ...excelCellStyle, fontSize: '0.75rem', color: '#6b7280' }}>
                      {student.codeEtudiant || '-'}
                    </td>
                    <td style={excelCellStyle}>{student.filiere || '-'}</td>
                    <td style={excelCellStyle}>{classe}</td>
                    <td style={{
                      ...excelCellStyle, textAlign: 'right', fontFamily: 'monospace',
                      // ✅ couleur orange si prix = 0
                      color: estPrixZero ? '#f59e0b' : '#1f2937',
                      fontWeight: estPrixZero ? '600' : 'normal'
                    }}>
                      {formatMoney(prixTotal)}
                    </td>
                    <td style={{
                      ...excelCellStyle, textAlign: 'right',
                      fontFamily: 'monospace', color: '#10b981', fontWeight: '600'
                    }}>
                      {formatMoney(totalPaye)}
                    </td>
                    <td style={{
                      ...excelCellStyle, textAlign: 'right', fontFamily: 'monospace',
                      fontWeight: '600',
                      // ✅ couleur mise à jour
                      color: estDesiste
                        ? '#dc2626'
                        : estPrixZero
                          ? '#f59e0b'
                          : reste > 0
                            ? '#ef4444'
                            : '#10b981'
                    }}>
                      {estDesiste
                        ? '🚫 Désisté'
                        : estTransfere && reste === 0
                          ? '🔄 Transféré'
                          : estPrixZero
                            ? '⚠️ Prix = 0'   // ✅ ici l'affichage spécial
                            : reste > 0
                              ? formatMoney(reste)
                              : '✅ Soldé'
                      }
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ background: '#f3f4f6', fontWeight: 'bold' }}>
                <td colSpan={4} style={{ ...excelCellStyle, textAlign: 'right' }}>
                  TOTAL ({etudiantsFiltres.length} étudiants)
                  {/* ✅ Mention des prix = 0 dans le footer */}
                  {nbPrixZero > 0 && (
                    <span style={{ fontSize: '0.75rem', color: '#92400e', marginLeft: '0.5rem', fontWeight: 'normal' }}>
                      (dont {nbPrixZero} à Prix = 0)
                    </span>
                  )}
                </td>
                <td style={{ ...excelCellStyle, textAlign: 'right', fontFamily: 'monospace' }}>
                  {formatMoney(grandTotalPrix)}
                </td>
                <td style={{ ...excelCellStyle, textAlign: 'right', fontFamily: 'monospace', color: '#10b981' }}>
                  {formatMoney(grandTotalPaye)}
                </td>
                <td style={{ ...excelCellStyle, textAlign: 'right', fontFamily: 'monospace', color: '#ef4444' }}>
                  {formatMoney(grandTotalReste)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};

const excelHeaderStyle = {
  padding: '0.75rem 1rem',
  textAlign: 'left',
  fontWeight: '600',
  color: '#374151',
  border: '1px solid #d1d5db',
  background: '#f3f4f6'
};

const excelCellStyle = {
  padding: '0.75rem 1rem',
  border: '1px solid #e5e7eb',
  color: '#1f2937'
};

export default StudentPaymentsTracker;